import { useState } from "react";

import { periscopeThemeKeyEvent } from "../../constants/index";
import {
  ThemePreference,
  getStoredThemePreference,
  resolveIsDarkMode,
  storeThemePreference,
} from "../theme-preference";

interface UseThemePreferenceResult {
  themePreference: ThemePreference;
  isDarkMode: boolean;
  handleThemeChange: (preference: ThemePreference) => void;
}

/**
 * Manages the light/dark theme preference: persists it and resolves it to a
 * boolean. A brand-new user's initial preference follows the OS once (see
 * getStoredThemePreference), after which the choice is always explicit.
 */
export const useThemePreference = (): UseThemePreferenceResult => {
  const [themePreference, setThemePreference] = useState<ThemePreference>(
    getStoredThemePreference,
  );
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() =>
    resolveIsDarkMode(themePreference),
  );

  const handleThemeChange = (preference: ThemePreference) => {
    storeThemePreference(preference);
    setThemePreference(preference);

    const resolved = resolveIsDarkMode(preference);
    setIsDarkMode(resolved);

    // Dispatch custom event so other components know display theme has changed
    window.dispatchEvent(
      new CustomEvent(periscopeThemeKeyEvent, {
        detail: { isDarkMode: resolved },
      }),
    );
  };

  return { themePreference, isDarkMode, handleThemeChange };
};
